import { FOCUSABLE_ELEMENT_SELECTOR } from './focusable-elements-selector';
import { hasNegativeTabIndex } from './has-negative-tabindex';
import { isElementVisible } from './is-element-visible';

export type GetTabbableElementsOptions = {
  /** Maximum number of descendants to return */
  limit?: number;
  /** Whether to return hidden elements */
  includeHidden?: boolean;
  /** Return only those elements that are not descendants of any of the given ancestors. If `ancestor` (1st param) is included in the array, it still includes it's descendant elements. */
  excludeAncestors?: Element[];
};

/**
 * Returns every tabbable descendants of the given ancestor element
 *
 */
export function getTabbableElements(
  ancestor: Element | Document = document,
  options: GetTabbableElementsOptions = {},
): HTMLElement[] {
  const focusableElements = Array.from(
    ancestor.querySelectorAll(FOCUSABLE_ELEMENT_SELECTOR),
  );
  if (focusableElements.length === 0) {
    return [];
  }

  const excludeAncestors = options.excludeAncestors?.filter(
    (excludedAncestor) =>
      ancestor !== excludedAncestor && ancestor.contains(excludedAncestor),
  );

  const returnElements: HTMLElement[] = [];

  for (const element of focusableElements) {
    const isHTMLElement = element instanceof HTMLElement;
    const hasPositiveTabindex = !hasNegativeTabIndex(element);
    const isOptionallyVisible =
      options.includeHidden || isElementVisible(element);
    const isNotInExcludedAncestor = !excludeAncestors?.find(
      (excludedAncestor) => excludedAncestor.contains(element),
    );

    if (
      isHTMLElement &&
      hasPositiveTabindex &&
      isOptionallyVisible &&
      isNotInExcludedAncestor
    ) {
      returnElements.push(element);
    }

    if (options.limit && returnElements.length === options.limit) {
      break;
    }
  }

  return returnElements;
}
